import { useEffect, useRef, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase, waitForTenantClient } from "../utils/supabaseClient";

/**
 * LIVE ATTENDANCE SHEETS (D9, 2026-09-24).
 *
 * When one staff member marks attendance, every other open copy of the same
 * sheet re-reads it within about a second. The backend's one attendance
 * writer (services/attendanceRecords → services/attendanceLive) sends a
 * DATA-FREE Realtime broadcast after every successful write; this hook listens
 * on the sheet's topic and calls `onChange` (debounced) so the screen re-reads
 * the sheet through the authenticated API.
 *
 * WHY NOT postgres_changes. attendance_records' only RLS policy is
 * service-role and this client holds the tenant ANON key with no session, so
 * postgres_changes delivered nothing to the browser — the class page's old
 * "live sync" reported SUBSCRIBED while receiving nothing, ever. A public
 * broadcast reaches the anon client; its payload names the sheet only.
 *
 * TOPICS — must equal the backend's (services/attendanceLive; both sides pin
 * the literal strings in a test):
 *   attendance:class:<slot_id>:<YYYY-MM-DD>
 *   attendance:event:<event_id>   (event / camp / clinic; the payload's `date`
 *                                  says which day — null = every day)
 *
 * STATUS is derived from the channel, never assumed: "connecting" until the
 * server confirms the join, "live" while joined, "offline" after an error, a
 * timeout, a close or the browser going offline. When a lost channel rejoins,
 * pings sent meanwhile are gone, so the rejoin itself triggers one re-read.
 */

export type AttendanceSheet =
  | { context: "class"; slotId: string; date: string }
  | { context: "event" | "camp" | "clinic"; eventId: string; date?: string | null };

export type AttendanceLiveStatus = "off" | "connecting" | "live" | "offline";

export const ATTENDANCE_PING_EVENT = "changed";
export const ATTENDANCE_LIVE_DEBOUNCE_MS = 300;

const day = (date?: string | null) => (date ? String(date).slice(0, 10) : "");

/** The Realtime topic of a sheet, or null when the sheet is not fully chosen yet. */
export function attendanceSheetTopic(sheet: AttendanceSheet | null | undefined): string | null {
  if (!sheet) return null;
  if (sheet.context === "class") {
    if (!sheet.slotId || !day(sheet.date)) return null;
    return `attendance:class:${sheet.slotId}:${day(sheet.date)}`;
  }
  if (!sheet.eventId) return null;
  return `attendance:event:${sheet.eventId}`;
}

// ---------------------------------------------------------------------------
// One channel per topic per tab. realtime-js returns the EXISTING channel for
// a topic, and a second subscribe() on it throws; a channel being removed
// stays listed until the server acknowledges the leave. So listeners share a
// channel, and a new join for a topic waits for that topic's pending leave
// (React StrictMode remounts, a day switched back and forth).
// ---------------------------------------------------------------------------

type ChannelState = "connecting" | "live" | "offline";

interface Listener {
  onPing: (payload: any) => void;
  onState: (state: ChannelState, rejoined: boolean) => void;
}

interface Entry {
  listeners: Set<Listener>;
  channel: RealtimeChannel | null;
  state: ChannelState;
  everJoined: boolean;
  closed: boolean;
}

const entries = new Map<string, Entry>();
const pendingLeaves = new Map<string, Promise<unknown>>();

function broadcastState(entry: Entry, state: ChannelState, rejoined = false) {
  entry.state = state;
  entry.listeners.forEach((l) => l.onState(state, rejoined));
}

async function join(topic: string, entry: Entry) {
  const leaving = pendingLeaves.get(topic);
  if (leaving) await leaving.catch(() => undefined);
  const ready = await waitForTenantClient();
  if (entry.closed) return;
  // The placeholder client has no Realtime server: say so, do not pretend.
  if (!ready) {
    broadcastState(entry, "offline");
    return;
  }

  const client = getSupabase();
  const channel = client
    .channel(topic)
    .on("broadcast", { event: ATTENDANCE_PING_EVENT }, (message: any) => {
      entry.listeners.forEach((l) => l.onPing(message?.payload));
    });
  entry.channel = channel;
  channel.subscribe((status: string) => {
    if (entry.closed) return;
    if (status === "SUBSCRIBED") {
      const rejoined = entry.everJoined;
      entry.everJoined = true;
      broadcastState(entry, "live", rejoined);
    } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
      broadcastState(entry, "offline");
    }
  });
}

function addListener(topic: string, listener: Listener): () => void {
  let entry = entries.get(topic);
  if (!entry) {
    entry = { listeners: new Set(), channel: null, state: "connecting", everJoined: false, closed: false };
    entries.set(topic, entry);
    entry.listeners.add(listener);
    const created = entry;
    join(topic, created).catch(() => {
      if (!created.closed) broadcastState(created, "offline");
    });
  } else {
    entry.listeners.add(listener);
    listener.onState(entry.state, false);
  }

  const mine = entry;
  return () => {
    mine.listeners.delete(listener);
    if (mine.listeners.size > 0) return;
    mine.closed = true;
    if (entries.get(topic) === mine) entries.delete(topic);
    if (mine.channel) {
      const leave = Promise.resolve(getSupabase().removeChannel(mine.channel)).finally(() => {
        if (pendingLeaves.get(topic) === leave) pendingLeaves.delete(topic);
      });
      pendingLeaves.set(topic, leave);
    }
  };
}

/** Test seam: forget every shared channel (jest module state between tests). */
export function __resetAttendanceLiveForTests() {
  entries.clear();
  pendingLeaves.clear();
}

/**
 * Re-read `sheet` whenever another screen writes to it.
 *
 * @param sheet the sheet on screen, or null while it is not chosen (no subscription)
 * @param onChange re-read the sheet. Called at most once per `debounceMs` burst.
 */
export function useAttendanceLive(
  sheet: AttendanceSheet | null | undefined,
  onChange: () => void,
  { debounceMs = ATTENDANCE_LIVE_DEBOUNCE_MS }: { debounceMs?: number } = {}
): { status: AttendanceLiveStatus } {
  const topic = attendanceSheetTopic(sheet);
  // A per-day event sheet ignores pings for another day (null = every day).
  const dayFilter = sheet && sheet.context !== "class" ? day(sheet.date) : "";
  const [status, setStatus] = useState<AttendanceLiveStatus>(topic ? "connecting" : "off");

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!topic) {
      setStatus("off");
      return undefined;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        if (active) onChangeRef.current();
      }, debounceMs);
    };

    setStatus("connecting");
    const remove = addListener(topic, {
      onPing: (payload) => {
        const pingDay = day(payload?.date);
        if (dayFilter && pingDay && pingDay !== dayFilter) return;
        schedule();
      },
      onState: (state, rejoined) => {
        if (!active) return;
        const online = typeof navigator === "undefined" || navigator.onLine !== false;
        setStatus(state === "live" && !online ? "offline" : state);
        if (rejoined) schedule();
      },
    });

    const goOffline = () => active && setStatus("offline");
    window.addEventListener("offline", goOffline);

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      window.removeEventListener("offline", goOffline);
      remove();
    };
  }, [topic, dayFilter, debounceMs]);

  return { status };
}

export default useAttendanceLive;
